<template>
    <div class="flex h-full">
        <!-- Left: plate list -->
        <div class="bg-white/10 text-white w-56 min-w-48 h-full overflow-y-auto p-2 border-r border-white/20 flex flex-col">
            <div class="flex justify-between items-center mb-2 flex-shrink-0">
                <span class="font-semibold text-sm text-gray-300">Plates</span>
                <button @click="addPlate"
                    class="bg-ui-active text-white px-2 py-1 rounded hover:opacity-90 text-xs">
                    Add
                </button>
            </div>
            <ul class="flex-1">
                <li v-for="(plate, index) in plates" :key="plate.id"
                    :class="[
                        'py-2 px-2 rounded cursor-pointer mb-1 flex items-center justify-between text-sm',
                        selectedEditPlate && selectedEditPlate.id === plate.id
                            ? 'bg-white/20 text-white border border-white/30'
                            : 'text-gray-400 hover:text-white hover:bg-white/10 bg-white/5 border border-white/10',
                    ]"
                    @click="selectPlateForEdit(plate, index)">
                    <span class="flex-1 truncate">{{ plate.name }}</span>
                    <button class="ml-2 text-red-400 hover:text-red-600"
                        title="Double-click to delete"
                        @dblclick.stop="deletePlate(index)"
                        @click.stop>
                        <font-awesome-icon icon="fa-solid fa-trash-can" />
                    </button>
                </li>
            </ul>
        </div>

        <!-- Right: selected plate settings -->
        <div class="flex-1 min-w-0 overflow-y-auto bg-transparent p-4">
            <div v-if="selectedEditPlate" class="flex w-full min-w-0 flex-col gap-4 max-w-none">

                <!-- Device -->
                <div class="border border-white/20 rounded-md p-3 space-y-3 bg-white/10">
                    <p class="text-gray-300 font-semibold text-xs">Device</p>
                    <div class="flex gap-2">
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">Name</span>
                            <input type="text" v-model="localPlate.name"
                                class="w-full bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40" />
                        </div>
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">Host</span>
                            <input type="text" v-model="localPlate.host"
                                class="w-full bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40" />
                        </div>
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">Size</span>
                            <select v-model="localPlate.size_name" @change="updatePlateSize"
                                class="w-full bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40">
                                <option v-for="(spec, ref) in pageSizes" :key="ref" :value="ref">{{ spec.title }}</option>
                            </select>
                        </div>
                    </div>
                    <label class="w-full basis-full flex items-center gap-2 text-xs text-gray-300 cursor-pointer">
                        <input v-model="localPlate.native" type="checkbox" @change="syncDeviceConnection"
                            class="rounded border-white/20 bg-white/15 text-ui-active focus:ring-ui-active" />
                        <span>Native device</span>
                    </label>
                    <div v-if="localPlate.native" class="flex items-center gap-3">
                        <label class="flex items-center gap-2">
                            <span class="text-xs text-gray-400">Native HTTP port</span>
                            <input v-model.number="localPlate.http_port" type="number" min="1" max="65535"
                                placeholder="8080" @change="syncDeviceConnection"
                                class="w-24 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40" />
                        </label>
                        <label class="flex items-center gap-2">
                            <span class="text-xs text-gray-400">Native Telnet port</span>
                            <input v-model.number="localPlate.telnet_port" type="number" min="1" max="65535"
                                placeholder="2323" @change="syncDeviceConnection"
                                class="w-24 bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40" />
                        </label>
                    </div>
                </div>

                <!-- HTTP -->
                <div class="border border-white/20 rounded-md p-3 space-y-3 bg-white/10">
                    <p class="text-gray-300 font-semibold text-xs">HTTP</p>
                    <div class="flex gap-2">
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">User</span>
                            <input type="text" v-model="localPlate.user"
                                class="w-full bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40" />
                        </div>
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">Password</span>
                            <input type="password" v-model="localPlate.pass"
                                class="w-full bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40" />
                        </div>
                    </div>
                    <button v-if="appSettings.tauri"
                        class="w-full px-3 py-1 rounded text-xs transition-colors"
                        :class="{
                            'bg-slate-600 hover:bg-slate-500 text-white': !httpTestStatus,
                            'bg-green-600 text-white': httpTestStatus === 'success',
                            'bg-red-600 text-white': httpTestStatus === 'error'
                        }"
                        :disabled="testingHttp"
                        @click="testHttpConnection">
                        <span v-if="testingHttp">Testing...</span>
                        <span v-else>Test HTTP</span>
                    </button>
                    <div v-if="httpTestMessage" class="text-xs"
                        :class="{
                            'text-green-400': httpTestStatus === 'success',
                            'text-red-400': httpTestStatus === 'error'
                        }">
                        {{ httpTestMessage }}
                    </div>
                </div>

                <!-- OpenHASP theme -->
                <div class="border border-white/20 rounded-md p-3 space-y-3 bg-white/10">
                    <p class="text-gray-300 font-semibold text-xs">Theme</p>
                    <div class="flex gap-2">
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">UI Theme</span>
                            <select v-model="themeOption"
                                class="w-full bg-white/15 border border-white/20 rounded px-2 py-1 text-white text-xs focus:outline-none focus:border-white/40">
                                <option v-for="t in themeChoices" :key="t.value" :value="t.value">{{ t.title }}</option>
                            </select>
                        </div>
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">Primary Color</span>
                            <input type="color" v-model="localPlate.primary_color" @change="onColorChange"
                                class="w-full h-8 bg-white/15 border border-white/20 rounded px-1 py-0.5 cursor-pointer" />
                        </div>
                        <div class="flex-1">
                            <span class="block text-xs text-gray-400 mb-1">Secondary Color</span>
                            <input type="color" v-model="localPlate.secondary_color" @change="onColorChange"
                                class="w-full h-8 bg-white/15 border border-white/20 rounded px-1 py-0.5 cursor-pointer" />
                        </div>
                    </div>
                </div>

                <GpioPlateProperties
                    :plate="localPlate"
                    :appSettings="appSettings"
                    @save="saveGpioConfig" />

                <!-- Actions -->
                <div class="flex gap-2">
                    <button @click="savePlate"
                        :disabled="isLoading"
                        class="flex-1 bg-ui-active text-white px-3 py-2 rounded hover:opacity-90 text-sm transition-colors disabled:opacity-50">
                        Save
                    </button>
                    <button v-if="appSettings.tauri" @click="fetchPlateDetails"
                        :disabled="isLoading"
                        class="flex-1 bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-500 text-sm transition-colors disabled:opacity-50">
                        {{ isLoading ? 'Fetching…' : 'Fetch from Device' }}
                    </button>
                </div>
                <div class="h-1 rounded" :class="{ 'loading-bar': isLoading }"></div>
            </div>

            <div v-else class="text-gray-500 text-center mt-10 text-sm italic">
                Select a plate from the list or add a new one.
            </div>
        </div>
    </div>
</template>

<script>
import { invoke } from '@tauri-apps/api/core';
import { defaultSettings, addPlate, deletePlate, updatePlate, haspThemes } from '../../../utils/store/settings';
import { lookupPageSize } from '../../../utils/lookupTables';
import { setActiveTheme } from '../../../Hasp/properties/ConfigManager.js';
import { getAllPlateTypes } from '../../../utils/plateTypeDB';
import { loadExternalThemes, externalThemeKey } from '../../../themes/themeRegistry.js';
import { deviceHttpArgs } from '../../../services/httpTransfer.js';
import GpioPlateProperties from './GpioPlateProperties.vue';
import { normalizeGpioConfig, serializeGpioConfig } from '../../../gpio/gpioPlateConfig.js';

export default {
    components: {
        GpioPlateProperties,
    },
    props: {
        plates: { type: Array, required: true },
        appSettings: { type: Object, required: true, default: () => ({ plate: null }) },
        popupState: { type: Boolean, default: false },
    },
    emits: ['update', 'update_active_plate', 'imageStoreUpdated'],
    data: () => ({
        selectedPlate:          null,
        selectedEditPlate:      null,
        selectedEditPlateIndex: null,
        localPlate:             {},
        isLoading:              false,
        pageSizes:              {},
        haspThemes,
        customThemes: [],
        testingHttp:            false,
        httpTestStatus:         null,
        httpTestMessage:        '',
    }),
    computed: {
        themeChoices() {
            return [
                ...haspThemes.map(theme => ({ value: theme.name, title: theme.title })),
                ...this.customThemes.map(theme => ({ value: externalThemeKey(theme.id), title: `${theme.name} (Library)` })),
            ];
        },
        themeOption: {
            get() {
                const selected = this.localPlate?.hasp_theme;
                if (selected && (haspThemes.some(theme => theme.name === selected) || String(selected).startsWith('custom:'))) return selected;
                const num = Number(this.localPlate?.hasp_theme_num);
                return haspThemes.find(t => t.option === num)?.name ?? 'haspdark';
            },
            set(val) {
                const found = haspThemes.find(t => t.name === val);
                this.localPlate.hasp_theme = val;
                this.localPlate.hasp_theme_num = found?.option ?? null;
                setActiveTheme(this.localPlate.hasp_theme, this.localPlate.primary_color, this.localPlate.secondary_color);
                this.autoSave();
            },
        },
    },
    async mounted() {
        this.customThemes = await loadExternalThemes();
        await this.getPageSizesFromDB();
        if (this.appSettings?.plate?.name && this.plates.length > 0) {
            this.selectedPlate = this.plates.find(p => p.name === this.appSettings.plate.name) || null;
        }
        if (this.plates.length > 0) {
            const active = this.selectedPlate || this.plates[0];
            const index  = this.plates.indexOf(active);
            if (index >= 0) this.selectPlateForEdit(active, index);
        }
    },
    watch: {
        async popupState(visible) {
            if (visible) {
                this.customThemes = await loadExternalThemes();
                await this.getPageSizesFromDB();
            }
        },
    },
    methods: {
        async getPageSizesFromDB() {
            const plates = await getAllPlateTypes();
            const sizes  = {};
            plates.forEach(p => {
                sizes[p.boardname] = {
                    title: p.title || p.boardname,
                    width: p.screenSize.width,
                    height: p.screenSize.height,
                    shape: p.shape ?? 'rectangular',
                };
            });
            Object.assign(sizes, lookupPageSize);
            this.pageSizes = sizes;
        },
        updatePlateSize() {
            const size = this.pageSizes[this.localPlate.size_name];
            if (size) {
                this.localPlate.width = size.width;
                this.localPlate.height = size.height;
                this.localPlate.shape = size.shape ?? 'rectangular';
            }
        },
        onColorChange() {
            setActiveTheme(this.localPlate.hasp_theme, this.localPlate.primary_color, this.localPlate.secondary_color);
            this.autoSave();
        },
        async autoSave() {
            // Always persist to localStorage
            Object.assign(this.selectedEditPlate, this.localPlate);
            await updatePlate(this.selectedEditPlateIndex, this.localPlate);
            this.$emit('update_active_plate', this.localPlate);

            // Also push to device when running in Tauri
            if (this.appSettings.tauri) {
                const themeOption = haspThemes.find(t => t.name === this.localPlate.hasp_theme)?.option;
                const args = { ...deviceHttpArgs(this.localPlate), context: 'config/hasp/' };
                const payload = {
                    color1: (this.localPlate.primary_color   ?? '#00b6ff').slice(0, 7),
                    color2: (this.localPlate.secondary_color ?? '#ff9966').slice(0, 7),
                };
                if (themeOption !== undefined) payload.theme = themeOption;
                try {
                    await invoke('api_post_command', { connArgs: args, payload });
                } catch (e) {
                    console.warn('Could not push HASP settings to device:', e);
                }
            }
        },
        async savePlate() {
            Object.assign(this.selectedEditPlate, this.localPlate);
            await updatePlate(this.selectedEditPlateIndex, this.localPlate);
            if (this.selectedPlate?.id === this.localPlate.id) {
                this.selectedPlate = this.localPlate;
                this.$emit('update_active_plate', this.localPlate);
            }
        },
        async saveGpioConfig(config) {
            this.localPlate.gpio_config = config;
            Object.assign(this.selectedEditPlate, { gpio_config: config });
            await updatePlate(this.selectedEditPlateIndex, this.localPlate);
            if (this.selectedPlate?.id === this.localPlate.id) {
                this.selectedPlate = this.localPlate;
                this.$emit('update_active_plate', this.localPlate);
            }
        },
        async fetchPlateDetails() {
            if (!this.appSettings.tauri) return;
            this.isLoading = true;
            try {
                const args = { ...deviceHttpArgs(this.localPlate), context: 'config/' };
                const response = await invoke('api_get_command', { connArgs: args });
                if (response.status < 400) {
                    const cfg = response.body;
                    if (cfg.hasp?.color1) this.localPlate.primary_color   = cfg.hasp.color1;
                    if (cfg.hasp?.color2) this.localPlate.secondary_color = cfg.hasp.color2;
                    const found = haspThemes.find(t => t.option === cfg.hasp?.theme);
                    if (found) this.localPlate.hasp_theme = found.name;
                    // Same stored shape as a GPIO load/save (serializeGpioConfig).
                    if (cfg.gpio) this.localPlate.gpio_config = serializeGpioConfig(normalizeGpioConfig(cfg.gpio));
                    this.localPlate.saved_config = cfg;
                    this.localPlate.loaded       = true;
                    Object.assign(this.selectedEditPlate, this.localPlate);
                    await updatePlate(this.selectedEditPlateIndex, this.localPlate);
                    if (this.selectedPlate?.id === this.localPlate.id) this.$emit('update_active_plate', this.localPlate);
                    setActiveTheme(this.localPlate.hasp_theme, this.localPlate.primary_color, this.localPlate.secondary_color);
                }
            } catch (e) {
                console.error('Fetch failed:', e);
            } finally {
                this.isLoading = false;
            }
        },
        addPlate() {
            const plates   = this.plates;
            const highestId = plates.length > 0 ? Math.max(...plates.map(p => p.id)) : 0;
            const newPlate  = { ...defaultSettings.plate };
            newPlate.id    = Number(highestId) + 1;
            newPlate.name  = 'plate' + newPlate.id;
            newPlate.host  = newPlate.name + '.local';
            plates.push(newPlate);
            addPlate(newPlate);
            this.selectPlateForEdit(newPlate, plates.length - 1);
        },
        deletePlate(index) {
            const plate = this.plates[index];
            this.plates.splice(index, 1);
            deletePlate(index, plate.name);
            if (this.selectedEditPlate?.id === plate.id) {
                this.selectedEditPlate      = null;
                this.selectedEditPlateIndex = null;
                this.localPlate             = {};
            }
        },
        selectPlateForEdit(plate, index) {
            this.selectedEditPlate      = plate;
            this.selectedEditPlateIndex = index;
            this.selectedPlate          = plate;
            const plateCopy = { ...plate };
            plateCopy.native = plateCopy.native ?? false;
            plateCopy.http_port = plateCopy.http_port ?? 8080;
            plateCopy.telnet_port = plateCopy.telnet_port ?? 2323;
            if (plateCopy.primary_color?.length   > 7) plateCopy.primary_color   = plateCopy.primary_color.slice(0, 7);
            if (plateCopy.secondary_color?.length > 7) plateCopy.secondary_color = plateCopy.secondary_color.slice(0, 7);
            Object.assign(this.localPlate, plateCopy);
            this.appSettings.plate = plateCopy;
            this.$emit('update_active_plate', plateCopy);
            setActiveTheme(plate.hasp_theme, plate.primary_color, plate.secondary_color);
            this.httpTestStatus  = null;
            this.httpTestMessage = '';
        },
        syncDeviceConnection() {
            const httpPort = Number(this.localPlate.http_port) || 8080;
            const telnetPort = Number(this.localPlate.telnet_port) || 2323;
            this.localPlate.http_port = httpPort;
            this.localPlate.telnet_port = telnetPort;
            this.localPlate.native = this.localPlate.native === true;
            Object.assign(this.selectedEditPlate, {
                native: this.localPlate.native,
                http_port: httpPort,
                telnet_port: telnetPort,
            });
            this.appSettings.plate = {
                ...this.appSettings.plate,
                native: this.localPlate.native,
                http_port: httpPort,
                telnet_port: telnetPort,
            };
            this.$emit('update_active_plate', this.appSettings.plate);
        },
        async testHttpConnection() {
            this.testingHttp    = true;
            this.httpTestStatus = null;
            this.httpTestMessage = '';
            try {
                const args = { ...deviceHttpArgs(this.localPlate), context: 'config/' };
                const response = await invoke('api_get_command', { connArgs: args });
                if (response.status < 400) {
                    this.httpTestStatus  = 'success';
                    this.httpTestMessage = `Connected (HTTP ${response.status})`;
                } else {
                    this.httpTestStatus  = 'error';
                    this.httpTestMessage = `HTTP ${response.status}`;
                }
            } catch (e) {
                this.httpTestStatus  = 'error';
                this.httpTestMessage = e.toString();
            } finally {
                this.testingHttp = false;
            }
        },
    },
};
</script>

<style scoped>
.loading-bar {
    background: linear-gradient(90deg, transparent, rgb(130, 130, 255), transparent);
    background-size: 200% 100%;
    animation: moveBlue 1.5s infinite ease-in-out;
}
@keyframes moveBlue {
    0%   { background-position: 200% 0; }
    50%  { background-position: 0% 0; }
    100% { background-position: -200% 0; }
}
</style>
